import { Bell, BellOff } from "lucide-react";

import type { AgentSignalsResponse } from "../api/types";
import { formatDateTime } from "../lib/format";
import { SignalPill } from "./SignalPill";

interface AgentSignalsSectionProps {
  signals: AgentSignalsResponse | undefined;
  isLoading: boolean;
  errorMessage: string | null;
  alarmEmailEnabled: boolean;
  subscribedAgentKeys: Set<string>;
  pendingAgentKey: string | null;
  onToggleAlarm: (agentKey: string, agentTitle: string, isSubscribed: boolean) => void;
}

function statusLine(
  signals: AgentSignalsResponse | undefined,
  isLoading: boolean,
  errorMessage: string | null,
): string {
  if (errorMessage) {
    return errorMessage;
  }
  if (isLoading) {
    return "Loading agent signals...";
  }
  if (signals?.generated_at) {
    return `${signals.items.length} agents updated ${formatDateTime(signals.generated_at)}`;
  }
  return "Waiting for the next scheduled worker run.";
}

export function AgentSignalsSection({
  signals,
  isLoading,
  errorMessage,
  alarmEmailEnabled,
  subscribedAgentKeys,
  pendingAgentKey,
  onToggleAlarm,
}: AgentSignalsSectionProps) {
  const items = signals?.items ?? [];

  return (
    <div className="agent-signals-section">
      <div className="companion-heading">
        <p className="panel-eyebrow">Agent Signals</p>
      </div>
      <div className={`companion-status${errorMessage ? " is-error" : ""}`}>
        {statusLine(signals, isLoading, errorMessage)}
      </div>
      {!alarmEmailEnabled && (
        <p className="agent-signals-note">
          Email alarms are not configured on this server yet - the bell toggles below will not
          send mail until SMTP is set up.
        </p>
      )}
      <div className="agent-signals-list">
        {items.length === 0 ? (
          <p className="empty-state">No stored agent signals yet for this symbol.</p>
        ) : (
          items.map((item) => {
            const isSubscribed = subscribedAgentKeys.has(item.key);
            const isPending = pendingAgentKey === item.key;
            return (
              <div className="agent-signal-row" key={item.key}>
                <div className="agent-signal-main">
                  <span className="agent-signal-title">{item.title}</span>
                  <SignalPill signal={item.signal} size="sm" />
                </div>
                {item.reason && <p className="agent-signal-reason">{item.reason}</p>}
                <button
                  type="button"
                  className={`bell-toggle${isSubscribed ? " is-active" : ""}`}
                  disabled={isPending}
                  aria-pressed={isSubscribed}
                  title={
                    isSubscribed
                      ? `Stop emailing me when ${item.title} changes`
                      : `Email me when ${item.title} turns BUY or SELL`
                  }
                  onClick={() => onToggleAlarm(item.key, item.title, isSubscribed)}
                >
                  {isSubscribed ? <Bell size={16} /> : <BellOff size={16} />}
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
